<script>
import MetisMenu from 'metismenujs/dist/metismenujs'

import { authComputed } from '@state/helpers'
import { authProtectedRoutes } from '@routes'
import { Auth } from 'aws-amplify'

/**
 * Application menu component
 *
 * Renders a two-level sidebar:
 *   Level 1 — collapsible groups (Detect & Respond, Threat Intelligence, …)
 *   Level 2 — sub-section headers + menu items (same look as the original flat menu)
 */
export default {
  components: {},
  props: {
    mode: {
      type: String,
      default: 'vertical',
    },
  },
  data() {
    return {
      userGroups: [],
      menuItems: authProtectedRoutes,
    }
  },
  computed: {
    ...authComputed,
    groupedMenu() {
      const groups = []
      const groupMap = {}
      let lastGroup = 'Other'
      let lastGroupIcon = 'circle'
      this.menuItems.forEach((item) => {
        if (!this.mustShow(item)) return
        if (item.group) {
          lastGroup = item.group
          lastGroupIcon = item.groupIcon || item.icon
        }
        const groupName = item.group || lastGroup
        if (!groupMap[groupName]) {
          groupMap[groupName] = {
            name: groupName,
            icon: item.groupIcon || lastGroupIcon,
            items: [],
          }
          groups.push(groupMap[groupName])
        }
        groupMap[groupName].items.push(item)
      })
      return groups
    },
  },
  mounted: function() {
    const that = this
    Auth.currentAuthenticatedUser().then((res) => {
      that.userGroups =
        res.signInUserSession.idToken.payload['cognito:groups']
    })

    this.$nextTick(() => {
      var menuRef = null
      if (this.mode === 'horizontal') {
        menuRef = new MetisMenu('#side-menu').on(
          'shown.metisMenu',
          function(event) {
            window.addEventListener('click', function menuClick(e) {
              if (!event.target.contains(e.target)) {
                menuRef.hide(event.detail.shownElement)
                window.removeEventListener('click', menuClick)
              }
            })
          }
        )
      } else {
        menuRef = new MetisMenu('#side-menu')
      }
      this.activateMenu()
    })
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        this.activateMenu()
      })
    },
  },
  methods: {
    hasItems(item) {
      return item && item.children !== undefined
        ? item.children.length > 0
        : false
    },
    mustShow(item) {
      let ret = false
      try {
        if (item.meta.hideFromMenu) return false
        if (item.meta.requiresUserGroups === undefined) ret = true
        if (
          item.meta.requiresUserGroups.some((group) =>
            this.userGroups.includes(group)
          )
        ) {
          ret = true
        }
      } catch (e) {}
      return ret
    },
    activateMenu() {
      // Clear all previous active states first
      var sideMenu = document.getElementById('side-menu')
      if (sideMenu) {
        sideMenu.querySelectorAll('.mm-active').forEach(function(el) {
          el.classList.remove('mm-active')
        })
        sideMenu.querySelectorAll('.active').forEach(function(el) {
          el.classList.remove('active')
        })
        sideMenu.querySelectorAll('.mm-show').forEach(function(el) {
          el.classList.remove('mm-show')
        })
      }

      var links = document.getElementsByClassName('side-nav-link-ref')
      var matchingMenuItem = null
      for (var i = 0; i < links.length; i++) {
        if (window.location.pathname === links[i].pathname) {
          matchingMenuItem = links[i]
          break
        }
      }

      const activeClass = this.mode === 'horizontal' ? 'active' : 'mm-active'
      const dropdownActiveClass =
        this.mode === 'horizontal' ? 'active' : 'mm-show'

      if (matchingMenuItem) {
        matchingMenuItem.classList.add('active')
        var el = matchingMenuItem.parentElement
        // Walk up the DOM tree, stop at the #side-menu root
        while (el && el.id !== 'side-menu') {
          if (el.tagName === 'LI') {
            el.classList.add(activeClass)
            // Only activate the DIRECT child anchor, not all descendants
            var directChildren = el.children
            for (var i = 0; i < directChildren.length; i++) {
              if (directChildren[i].classList.contains('has-dropdown')) {
                directChildren[i].classList.add('active')
              }
            }
          } else if (el.tagName === 'UL') {
            el.classList.add(dropdownActiveClass)
          }
          el = el.parentElement
        }
      }
    },
  },
}
</script>

<template>
  <ul id="side-menu" class="metismenu">
    <template v-for="group in groupedMenu">

      <!-- Single-item group (e.g. Home) — flat, no wrapper -->
      <li
        v-if="group.items.length === 1 && !hasItems(group.items[0]) && !group.items[0].header"
        :key="'g-' + group.name"
        class="side-nav-item"
      >
        <router-link
          tag="a"
          :to="group.items[0].path"
          class="side-nav-link side-nav-link-ref"
        >
          <feather v-if="group.icon" :type="group.icon" />
          <span>{{ group.items[0].name }}</span>
        </router-link>
      </li>

      <!-- Multi-item group — collapsible -->
      <li v-else :key="'g-' + group.name" class="side-nav-item">
        <a href="javascript:void(0);" class="side-nav-link has-dropdown">
          <feather v-if="group.icon" :type="group.icon" />
          <span>{{ group.name }}</span>
          <span class="menu-arrow"></span>
        </a>
        <ul class="nav-second-level" aria-expanded="false">
          <template v-for="item in group.items">

            <!-- Sub-section header -->
            <li
              v-if="item.header"
              :key="'sh-' + item.path"
              class="side-nav-item"
            >
              <p class="menu-title mb-0">{{ item.header }}</p>
            </li>

            <!-- Item WITH children (e.g. Filtered > Open/Closed) -->
            <li
              v-if="hasItems(item)"
              :key="'ic-' + item.path"
              class="side-nav-item"
            >
              <a href="javascript:void(0);" class="side-nav-link-a-ref has-dropdown">
                <feather v-if="item.icon" :type="item.icon" />
                <span>{{ item.name }}</span>
                <span class="menu-arrow"></span>
              </a>
              <ul class="nav-third-level" aria-expanded="false">
                <li
                  v-for="subitem in item.children"
                  :key="'c-' + subitem.path"
                >
                  <router-link
                    tag="a"
                    :to="`${item.path}/${subitem.path}`"
                    class="side-nav-link-ref"
                  >{{ subitem.name }}</router-link>
                </li>
              </ul>
            </li>

            <!-- Leaf item (no children) -->
            <li
              v-if="!hasItems(item)"
              :key="'il-' + item.path"
            >
              <router-link
                tag="a"
                :to="item.path"
                class="side-nav-link-ref"
              >
                <feather v-if="item.icon" :type="item.icon" />
                <span>{{ item.name }}</span>
              </router-link>
            </li>

          </template>
        </ul>
      </li>

    </template>
  </ul>
</template>

<style>
/*
 * Sidebar group hierarchy overrides.
 * Works WITH the existing _left-menu.scss styles, not against them.
 *
 * Level 1: Group headers  — styled by existing #sidebar-menu > ul > li > a rules
 * Level 2: Sub-section labels — use existing .menu-title class
 * Level 3: Items inside groups — keep icons, use existing .nav-second-level li a rules
 * Level 4: Children — use existing .nav-third-level rules
 */

/* ── Level 1: Group headers — reduce left padding so children are indented deeper ── */
#sidebar-menu > ul > li > a {
  padding: 5px 20px 5px 15px;
}

/* ── Level 2: Sub-section headers inside groups ── */
#sidebar-menu .nav-second-level > .side-nav-item > .menu-title {
  padding: 12px 20px 4px 20px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8391a2;
  margin: 0;
}
#sidebar-menu .nav-second-level > .side-nav-item:first-child > .menu-title {
  padding-top: 5px;
}

/* ── Level 3: Items inside groups — indented more than group headers ── */
#sidebar-menu .nav-second-level li a {
  padding: 8px 20px 8px 30px;
}
#sidebar-menu .nav-second-level li a svg {
  width: 16px !important;
  height: 16px !important;
  margin-right: 8px;
}

/* ── Level 4: Children (Open, Closed, etc.) ── */
#sidebar-menu .nav-third-level {
  padding-left: 12px;
}

/* ── Second-level list: no extra left shift ── */
#sidebar-menu > ul > li > ul.nav-second-level {
  padding-left: 0;
}

/* ── Menu arrows: vertically centered against text at ALL levels ── */
#sidebar-menu .menu-arrow {
  top: 50% !important;
  transform: translateY(-50%) !important;
}
#sidebar-menu li.mm-active > a > .menu-arrow {
  transform: translateY(-50%) rotate(90deg) !important;
}
</style>
